import { ref } from "vue";

import type { components as OpenAPI } from "@/autogen/openapi";
import api from "@/lib/api/base";
import type { FilterParameters } from "@/lib/api/queries/generic";
import { ok } from "@/lib/openapi";

import { removeToastById, showToast } from "../toasts";

const TIMEOUT_BEFORE_SHOWING_POPUP_IN_MILLISECONDS = 5 * 60 * 1_000; // 5 minutes

const pollStatus = async (id: string, callback?: () => void) => {
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const data = await ok(
      api.get("/exports/{id}", {
        params: { path: { id } },
      })
    );

    if (data.status === "ready") {
      showToast({
        type: "success",
        title: `Export successful!`,
      });

      if (data.url) {
        window.location.href = data.url;
      }
      if (callback) {
        callback();
      }

      break;
    }

    await new Promise((resolve) => setTimeout(resolve, 1_000));
  }
};

type State =
  | {
      status: "not_started" | "confirming" | "pending";
    }
  | {
      status: "done";
      url: string;
    };

export const useExport = (
  collections: OpenAPI["schemas"]["ExportCollection"][] | []
) => {
  const state = ref<State>({ status: "not_started" });

  const confirm = () => {
    if (collections.length === 0) {
      return;
    }

    state.value = { status: "confirming" };
  };

  const process = async (
    parameters: FilterParameters | undefined,
    columns?: string[],
    format: "csv" | "json" = "csv"
  ) => {
    if (collections.length === 0) {
      return;
    }

    const pendingToastId = showToast({
      type: "pending",
      title: `Exporting`,
    });

    state.value = { status: "pending" };

    const data = await ok(
      api.post("/exports", {
        body: { collections, parameters, columns, format },
      })
    );

    if (data.status === "ready") {
      if (data.url) {
        state.value = { status: "done", url: data.url };

        removeToastById(pendingToastId);

        showToast({
          type: "success",
          title: `Export successful!`,
        });
      }

      return;
    }

    const timeout = setTimeout(() => {
      showToast({
        type: "inform",
        title: `Export is taking longer than expected`,
        message: `Our system seems to be under load right now, we'll email you the resulting data export when it's ready.`,
      });
    }, TIMEOUT_BEFORE_SHOWING_POPUP_IN_MILLISECONDS);

    pollStatus(data.id, () => {
      if (data.url) {
        state.value = { status: "done", url: data.url };

        removeToastById(pendingToastId);

        showToast({
          type: "success",
          title: `Export successful!`,
        });
      }

      clearTimeout(timeout);
    });
  };

  return {
    collections,

    process,
    confirm,

    get state() {
      return state.value;
    },
    set state(next) {
      state.value = next;
    },
  };
};
